import { useState, useEffect, useRef } from 'react';
import WhatWeDoPage from './WhatWeDoPage'; 
import ContactPage from './ContactPage';
import AboutPage from './AboutPage';
import ArticlesPage from './ArticlesPage';


const Header = ({ onNavigate }) => {

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [menuStage, setMenuStage] = useState('closed');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activePage, setActivePage] = useState(null); 
  const [pageAnimation, setPageAnimation] = useState('entering'); 
  const menuRef = useRef(null);

  // System Status Code logic
  // const [statusCode, setStatusCode] = useState('200 OK');
  // useEffect(() => {
  //   // Generate a subtle, glitching status code update
  //   const interval = setInterval(() => {
  //     const codes = ['200 OK', '302 REDIRECT', '401 AUTH', '202 ACCEPT'];
  //     setStatusCode(codes[Math.floor(Math.random() * codes.length)]);
  //   }, 5000 + Math.random() * 2000); // 5 to 7 seconds

  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Real-time date updates when menu is open (UNCHANGED)
  useEffect(() => {
    if (isMenuOpen) {
      setCurrentDate(new Date());
      const minuteInterval = setInterval(() => {
        setCurrentDate(new Date());
      }, 60000); 

      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const timeUntilMidnight = tomorrow - now;

      const midnightTimeout = setTimeout(() => {
        setCurrentDate(new Date());
        const dailyInterval = setInterval(() => {
          setCurrentDate(new Date());
        }, 86400000);
        return () => clearInterval(dailyInterval);
      }, timeUntilMidnight);

      return () => {
        clearInterval(minuteInterval);
        clearTimeout(midnightTimeout);
      };
    }
  }, [isMenuOpen]);

  const openMenu = () => {
    setIsMenuOpen(true);
    setMenuStage('opening');
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    setMenuStage('closing');
    setTimeout(() => {
      setIsMenuOpen(false);
      document.body.style.overflow = 'unset';
    }, 600);
  };

  const handleNavigation = (page) => {
    setActivePage(page);
    setPageAnimation('entering');
    closeMenu();
  };

  const handleClosePage = () => {
    setPageAnimation('exiting');
    setTimeout(() => {
      setActivePage(null);
    }, 600);
  };

  // Calendar data function (UNCHANGED)
  const getCalendarData = () => {
    const today = currentDate;
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); 
    
    const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    const days = [];
    
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: '', isEmpty: true });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === today.getDate() && 
                      currentMonth === today.getMonth() && 
                      currentYear === today.getFullYear();
      days.push({ 
        day, 
        isToday,
        isEmpty: false,
        isPast: day < today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear(),
        isFuture: day > today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear()
      });
    }
    
    return {
      month: months[currentMonth],
      year: currentYear,
      weekdays,
      days,
      lastUpdated: today.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const calendar = getCalendarData();

  return (
    <>
      {/* Main Header (Command Line Bar) */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-cyan-500/30 ${
        hasScrolled 
          ? 'bg-black/90 backdrop-blur-md py-3' // Increased opacity on scroll
          : 'bg-transparent py-4' // Slightly taller when not scrolled
      }`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex justify-between items-center h-8 font-mono">

            {/* LEFT: Menu/Command Button & Live Status */}
            <div className="flex items-center gap-4">
              <button 
                onClick={openMenu}
                className={`text-cyan-400 text-xs tracking-widest uppercase px-3 py-1 border border-cyan-400/50 hover:bg-cyan-400/10 transition-colors duration-200 ${
                  hasScrolled ? 'text-white border-white/30 hover:bg-white/10' : ''
                }`}
              >
                [ Menu ]
              </button>
              
              {/* <span className={`text-xs transition-colors duration-500 ${
                statusCode.startsWith('2') ? 'text-green-500/80' : 'text-orange-500/80' // Color based on status
              }`}>
                {statusCode}
              </span> */}
            </div>

            {/* CENTER: Logo/System Name */}
            <div className="flex items-center">
              <button 
                onClick={() => onNavigate('home')}
                className="text-white hover:text-cyan-400 transition-colors duration-300 flex items-center gap-2"
              >
                <img 
                  src="/icons/favicon-32x32.png" 
                  alt="Nixque Icon" 
                  className={`w-5 h-5 transition-transform duration-300 ${hasScrolled ? 'scale-100' : 'scale-110'}`} 
                />
                <span className="text-sm font-bold tracking-widest uppercase">
                  NIXQUE
                </span>
              </button>
            </div>

            {/* RIGHT: Live Time & Email Button */}
            <div className="flex items-center gap-4">
              <span className={`text-white/60 text-xs transition-opacity duration-300 ${hasScrolled ? 'opacity-100' : 'opacity-80'}`}>
                {currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              
              <button 
                onClick={() => window.open('mailto:contact@nixque.com', '_blank')}
                className={`p-1.5 border border-cyan-400/50 hover:bg-cyan-400/10 transition-colors duration-200 ${
                  hasScrolled ? 'border-white/30 hover:bg-white/10' : ''
                }`}
              >
                <svg viewBox="0 0 10 8" fill="none" className="w-3 h-3 text-cyan-400 group-hover:text-white transition-colors duration-300">
                  <rect x="0.5" y="1" width="9" height="6" rx="0.5" stroke="currentColor"></rect>
                  <path d="M0.5 1.5L4.4453 4.1302C4.7812 4.35413 5.2188 4.35413 5.5547 4.1302L9.5 1.5" stroke="currentColor"></path>
                </svg>
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* --- MENU OVERLAY (UNCHANGED FUNCTIONALITY, ADDED STYLES) --- */}
      {isMenuOpen && (
        <div 
          ref={menuRef}
          className={`fixed inset-0 z-50 bg-gray-900 text-white overflow-hidden ${
            menuStage === 'closing' ? 'menu-closing' : ''
          }`}
        >
          {/* Quantum Grid Background (UNCHANGED) */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(20)].map((_, i) => (
              <div
                key={`h-${i}`}
                className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
                style={{
                  top: `${(i / 19) * 100}%`,
                  transform: menuStage === 'closing' ? 'translateX(0)' : 'translateX(-100%)',
                  animation: menuStage === 'closing' ? 
                    `slideOutHorizontal 0.6s ease-in ${i * 0.01}s forwards` :
                    `slideInHorizontal 0.8s ease-out ${i * 0.02}s forwards`
                }}
              />
            ))}
            {[...Array(25)].map((_, i) => (
              <div
                key={`v-${i}`}
                className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-400 to-transparent"
                style={{
                  left: `${(i / 24) * 100}%`,
                  transform: menuStage === 'closing' ? 'translateY(0)' : 'translateY(-100%)',
                  animation: menuStage === 'closing' ?
                    `slideOutVertical 0.6s ease-in ${i * 0.008}s forwards` :
                    `slideInVertical 0.8s ease-out ${i * 0.015}s forwards`
                }}
              />
            ))}
          </div>

          {/* Scanning Line (UNCHANGED) */}
          <div
            className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-lg shadow-cyan-400/50"
            style={{
              top: '50%',
              animation: menuStage === 'closing' ? 
                'menuScanOut 0.6s ease-in forwards' :
                'menuScan 1.2s ease-in-out 0.3s forwards'
            }}
          />

          {/* Main Content Layout - Scrollable on mobile */}
          <div className="h-full flex flex-col lg:flex-row relative z-10">
            {/* Navigation Sidebar */}
            <div className="w-full lg:w-[38%] border-b lg:border-b-0 lg:border-r border-white/10 p-4 lg:p-8 flex-shrink-0"
                 style={{ 
                    animation: menuStage === 'closing' ? 
                      'slideOutLeft 0.6s ease-in forwards' :
                      'slideInLeft 0.8s ease-out 0.4s both' 
                  }}>
              <div className="h-full flex flex-col">
                {/* Mobile Close Button */}
                <div className="lg:hidden flex justify-end mb-4">
                  <button 
                    onClick={closeMenu}
                    className="px-4 py-2 rounded-xl border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-all duration-300 font-mono text-sm"
                    style={{ animation: 'fadeIn 0.6s ease-out 0.8s both' }}
                  >
                    EXIT
                  </button>
                </div>

                <div className="flex-1">
                  <h2 className="text-cyan-400 text-xs font-mono mb-4 lg:mb-6 tracking-widest flex items-center gap-2"
                      style={{ animation: 'fadeInUp 0.6s ease-out 0.6s both' }}>
                    <div className="relative">
                      <img 
                        src="/icons/favicon-32x32.png" 
                        alt="Nixque Icon" 
                        className="w-6 h-6" 
                      />
                    </div>
                    NIXQUE
                  </h2>
                    
                  <div className="mb-6 lg:mb-8">
                    <h3 className="text-lg lg:text-xl font-bold text-white mb-4 pb-2 border-b border-cyan-400"
                        style={{ animation: 'fadeInUp 0.6s ease-out 0.7s both' }}>
                      Virtually Limitless
                    </h3>
                    
                    <ul className="space-y-2">
                      {['What We Do','About Us', 'Articles', 'Get in Touch'].map((item, index) => (
                        <li key={item}>
                          <button 
                            onClick={() => {
                              if (item === 'What We Do') handleNavigation('what-we-do');
                              if (item === 'About Us') handleNavigation('about');
                              if (item === 'Get in Touch') handleNavigation('contact');
                              if (item === 'Articles') handleNavigation('articles');
                            }}
                            className="text-white/60 hover:text-white transition-all duration-300 text-sm lg:text-base py-1 px-2 rounded-lg hover:bg-white/5 w-full text-left font-mono"
                            style={{ animation: `fadeInUp 0.6s ease-out ${0.8 + index * 0.1}s both` }}
                          >
                            {`> ${item}`}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="text-white/40 text-xs font-mono"
                      style={{ animation: 'fadeIn 0.6s ease-out 1.1s both' }}>
                  <p>© 2025 NIXQUE</p>
                </div>
              </div>
            </div>

            {/* Scrollable Calendar Content Area (UNCHANGED) */}
            <div className="flex-1 lg:w-[62%] overflow-y-auto"
                 style={{ 
                    animation: menuStage === 'closing' ?
                      'slideOutRight 0.6s ease-in forwards' :
                      'slideInRight 0.8s ease-out 0.4s both' 
                  }}>
              <div className="p-4 lg:p-8 max-w-6xl mx-auto h-full flex flex-col">
                {/* Desktop Header with Close Button */}
                <div className="hidden lg:flex items-center justify-between mb-8">
                  <div className="flex-1"></div>
                  
                  <div className="flex-1 flex justify-end">
                    <button 
                      onClick={closeMenu}
                      className="px-5 py-2.5 border border-white/20 text-white hover:bg-white/10 transition-all duration-300 font-mono text-sm tracking-wide hover:border-cyan-400/30 hover:text-cyan-300"
                      style={{ animation: 'fadeIn 0.6s ease-out 0.8s both' }}
                    >
                      EXIT
                    </button>
                  </div>
                </div>

                {/* Calendar Container */}
                <div className="flex-1 flex items-center justify-center p-4 lg:p-8"
                     style={{ animation: 'fadeInUp 0.8s ease-out 0.7s both' }}>
                  <div className="w-full max-w-md lg:max-w-md bg-white/5 backdrop-blur-sm rounded-none border border-white/10 p-6 lg:p-6 shadow-2xl">
                    {/* Calendar Header with Live Time */}
                    <div className="text-center mb-4">
                      <h2 className="text-xl lg:text-xl font-bold text-white mb-1 font-mono tracking-tight">
                        {calendar.month} {calendar.year}
                      </h2>
                      <div className="flex items-center justify-center gap-2">
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                          <span className="text-green-400 text-xs font-mono">LIVE</span>
                        </div>
                        <span className="text-cyan-400 text-xs font-mono">
                          {currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>

                    {/* Weekday Headers - Minimal */}
                    <div className="grid grid-cols-7 gap-0.5 mb-1">
                      {calendar.weekdays.map((day) => (
                        <div key={day} className="text-center py-0.5">
                          <span className="text-cyan-400 text-[10px] font-mono font-medium tracking-wide">
                            {day}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Calendar Grid - Compact */}
                    <div className="grid grid-cols-7 gap-0.5">
                      {calendar.days.map((day, index) => (
                        <div
                          key={index}
                          className={`aspect-square rounded-none flex items-center justify-center transition-all duration-200 ${
                            day.isEmpty
                              ? 'opacity-20'
                              : day.isToday
                              ? 'bg-cyan-500/30 border border-cyan-400/50 shadow shadow-cyan-500/20'
                              : day.isPast
                              ? 'bg-white/2 border border-white/5 text-white/30'
                              : 'bg-white/5 border border-white/10 hover:bg-white/8 hover:border-cyan-400/20'
                          }`}
                        >
                          {!day.isEmpty && (
                            <span className={`text-[10px] font-medium font-mono ${
                              day.isToday 
                                ? 'text-cyan-200' 
                                : day.isPast
                                ? 'text-white/30'
                                : 'text-white/80'
                            }`}>
                              {day.day}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Minimal Footer */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-cyan-500/30 border border-cyan-400/50"></div>
                          <span className="text-white/60 text-xs font-mono">TODAY</span>
                        </div>
                      </div>
                      
                      <div className="text-cyan-300 text-xs font-mono">
                        SYNC: {currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced CSS Animations (UNCHANGED) */}
          <style jsx>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(-30px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes slideOutLeft {
              to {
                opacity: 0;
                transform: translateX(-30px);
              }
            }
            
            @keyframes slideInRight {
              from {
                opacity: 0;
                transform: translateX(30px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes slideOutRight {
              to {
                opacity: 0;
                transform: translateX(30px);
              }
            }
            
            @keyframes slideInHorizontal {
              to { transform: translateX(0); }
            }
            
            @keyframes slideOutHorizontal {
              to { transform: translateX(100%); }
            }
            
            @keyframes slideInVertical {
              to { transform: translateY(0); }
            }
            
            @keyframes slideOutVertical {
              to { transform: translateY(100%); }
            }
            
            @keyframes menuScan {
              0% {
                left: -100%;
                right: 100%;
              }
              50% {
                left: 0%;
                right: 0%;
              }
              100% {
                left: 100%;
                right: -100%;
              }
            }
            
            @keyframes menuScanOut {
              0% {
                left: 100%;
                right: -100%;
              }
              50% {
                left: 0%;
                right: 0%;
              }
              100% {
                left: -100%;
                right: 100%;
              }
            }
          `}</style>
        </div>
      )}

      {/* Page Components (UNCHANGED) */}
      {activePage === 'what-we-do' && (
        <WhatWeDoPage 
          onClose={handleClosePage}
          animationStage={pageAnimation}
          onNavigate={handleNavigation}
        />
      )}

      {activePage === 'about' && (
        <AboutPage 
          onClose={handleClosePage}
          animationStage={pageAnimation}
          onNavigate={handleNavigation}
        />
      )}

      {activePage === 'articles' && (
        <ArticlesPage 
          onClose={handleClosePage}
          animationStage={pageAnimation}
          onNavigate={handleNavigation}
        />
      )}

      {activePage === 'contact' && (
        <ContactPage 
          onClose={handleClosePage}
          animationStage={pageAnimation}
          onNavigate={handleNavigation}
        />
      )}
    </>
  );
};

export default Header;